import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl } from '../utils/api/config';
import CompactShareButton from './CompactShareButton';
import { formatEngagementCount } from '../utils/contentEngagement';
import './RelatedVillageNews.css';

/**
 * RelatedVillageNews Component
 * Displays related news from the same village with pagination
 */
function RelatedVillageNews({ villageId, currentNewsId, villageName }) {
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  const fetchRelatedNews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/news/village/${villageId}?limit=100`));
      const data = await response.json();

      console.log('📰 RelatedVillageNews: Fetched related news:', data);

      if (data.success) {
        // Filter out the current news and limit results
        const filtered = data.data.filter(news => news._id !== currentNewsId);
        setRelatedNews(filtered);
        setCurrentPage(1);
      } else {
        setRelatedNews([]);
      }
    } catch (error) {
      console.error('Error fetching related news:', error);
      setRelatedNews([]);
    } finally {
      setLoading(false);
    }
  }, [currentNewsId, villageId]);

  useEffect(() => {
    fetchRelatedNews();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchRelatedNews]);

  if (loading) {
    return (
      <div className="related-news-section">
        <h2>संबंधित खबरें</h2>
        <div className="loading-text">लोड हो रहा है...</div>
      </div>
    );
  }

  if (!relatedNews || relatedNews.length === 0) {
    return null;
  }

  // Pagination logic
  const totalPages = Math.ceil(relatedNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedNews = relatedNews.slice(startIndex, endIndex);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="related-news-section">
      <h2>{villageName} की अन्य खबरें</h2>

      <div className="related-news-grid">
        {displayedNews.map(news => (
          <article key={news._id} className="related-news-card">
            <Link
              to={`/village-news/${news._id}`}
              className="related-news-link"
              onClick={() => window.scrollTo(0, 0)}
            >
            <div className="related-news-image">
              <img src={news.image} alt={news.title} />
              <span className="related-category">{news.category}</span>
            </div>
            <div className="related-news-content">
              <h3>{news.title}</h3>
              <p>{news.shortDescription}</p>
              <div className="related-news-meta">
                <span>
                  {new Date(news.createdAt).toLocaleDateString('hi-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
                <span>{formatEngagementCount(news.views)} views</span>
              </div>
            </div>
            </Link>
            <div className="related-news-actions">
              <CompactShareButton
                title={news.title}
                url={`/village-news/${news._id}`}
                description={news.shortDescription}
                contentId={news._id}
              />
            </div>
          </article>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button
            className="pagination-btn prev-btn"
            onClick={handlePrevious}
            disabled={currentPage === 1}
          >
            ← पिछला
          </button>

          <div className="pagination-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                onClick={() => handlePageClick(pageNum)}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button
            className="pagination-btn next-btn"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            अगला →
          </button>
        </div>
      )}

      {/* Page Info */}
      <div className="pagination-info">
        <span>
          पृष्ठ {currentPage} / {totalPages} | कुल {relatedNews.length} खबरें
        </span>
      </div>
    </section>
  );
}

export default RelatedVillageNews;
